import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Input, Modal, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { TeamOutlined } from '@ant-design/icons';
import {
  COMPETENCY_PERMS as P,
  COMPETENCY_VIEW_ANY,
  useCompetencyAction,
  useCompetencyAssessments,
  useCompetencyCan,
  useCompetencyOverview,
  useEffectivenessList,
  useGapMatrix,
  useJobProfiles,
  useTrainingSchedule,
} from '@/hooks/useCompetency';
import { competencyService, type CompetencyEmployeeRow } from '@/services/competency.service';
import departmentService from '@/services/department.service';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE } from './competencyDisplay';

const { Text } = Typography;

const CompetencyPage: React.FC = () => {
  const navigate = useNavigate();
  const canView = useCompetencyCan(...COMPETENCY_VIEW_ANY);
  const canTraining = useCompetencyCan(P.trainingView, P.view, P.trainingManage);
  const canManageJd = useCompetencyCan(P.jobProfileManage);
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState<string>();
  const [profileId, setProfileId] = useState<string>();
  const [selected, setSelected] = useState<string[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignProfile, setAssignProfile] = useState<string>();
  const year = new Date().getFullYear();

  const { data: employees = [], isLoading } = useCompetencyOverview({ department_id: departmentId, job_profile_id: profileId }, canView);
  const { data: profiles = [] } = useJobProfiles();
  const { data: departments = [] } = useQuery({ queryKey: ['departments', 'selection'], queryFn: () => departmentService.getForSelection(), staleTime: 300_000 });
  const { data: gap } = useGapMatrix(undefined, canView);
  const { data: overdue = [] } = useEffectivenessList({ status: 'Open', overdue: '1' }, canTraining);
  const { data: schedule } = useTrainingSchedule(year);
  const { data: drafts = [] } = useCompetencyAssessments({ status: 'Draft' });
  const { data: submitted = [] } = useCompetencyAssessments({ status: 'Submitted' });

  const assign = useCompetencyAction(
    (v: { profileId: string; ids: string[] }) => competencyService.assignJobProfile(v.profileId, v.ids),
    'Job description assigned'
  );

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return employees;
    return employees.filter((e) => [e.full_name, e.employee_number, e.job_title].some((v) => String(v ?? '').toLowerCase().includes(s)));
  }, [employees, search]);

  const withoutJd = employees.filter((e) => !e.job_profile_id).length;

  const columns: ColumnsType<CompetencyEmployeeRow> = [
    { title: 'Emp. No.', dataIndex: 'employee_number', width: 110 },
    {
      title: 'Name',
      dataIndex: 'full_name',
      render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/employees/${r.id}`)}>{v}</a>,
    },
    { title: 'Department', dataIndex: 'department_name' },
    { title: 'Job title', dataIndex: 'job_title' },
    {
      title: 'Job description',
      dataIndex: 'job_profile_title',
      render: (v, r) => (v ? <a onClick={() => navigate(`${COMPETENCY_BASE}/job-profiles/${r.job_profile_id}`)}>{v}</a> : <Tag>Not assigned</Tag>),
    },
    { title: 'Trainings', dataIndex: 'trainings_completed', width: 95, align: 'right' },
    { title: 'Certificates', dataIndex: 'active_certifications', width: 105, align: 'right' },
    { title: 'Last assessed', dataIndex: 'last_assessment_date', width: 120, render: (v) => (v ? formatDate(v) : '-') },
    {
      title: 'Confirmation',
      dataIndex: 'confirmation_status',
      width: 120,
      render: (v) => (v ? <Tag color={v === 'Confirmed' ? 'green' : 'gold'}>{v}</Tag> : '-'),
    },
  ];

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <CompetencyNav title="Competency & Training" />
        <Alert type="info" showIcon message="Open Assessments or Effectiveness to evaluate the employees who report to you." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav title="Competency & Training" />
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={4}>
          <Card size="small">
            <Statistic title="Without job description" value={withoutJd} valueStyle={{ color: withoutJd ? '#d46b08' : undefined }} />
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small" hoverable onClick={() => navigate(`${COMPETENCY_BASE}/matrix`)}>
            <Statistic title="Competency gaps" value={(gap?.summary.Gap ?? 0) + (gap?.summary.Expired ?? 0)} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small" hoverable onClick={() => navigate(`${COMPETENCY_BASE}/matrix`)}>
            <Statistic title="Expiring (60 days)" value={gap?.summary.Expiring ?? 0} valueStyle={{ color: '#d48806' }} />
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small" hoverable onClick={() => navigate(`${COMPETENCY_BASE}/effectiveness`)}>
            <Statistic title="Effectiveness overdue" value={overdue.length} valueStyle={{ color: overdue.length ? '#cf1322' : undefined }} />
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small" hoverable onClick={() => navigate(`${COMPETENCY_BASE}/schedule`)}>
            <Statistic
              title={`Schedule ${year} achieved`}
              value={schedule?.summary.achievement_percent ?? 0}
              suffix="%"
            />
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small" hoverable onClick={() => navigate(`${COMPETENCY_BASE}/assessments`)}>
            <Statistic title="Assessments open" value={drafts.length + submitted.length} suffix={<Text type="secondary" style={{ fontSize: 12 }}>{submitted.length} with HR</Text>} />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <TeamOutlined /> Employees
          </Space>
        }
        extra={
          canManageJd ? (
            <Button type="primary" disabled={!selected.length} onClick={() => setAssignOpen(true)}>
              Assign job description ({selected.length})
            </Button>
          ) : null
        }
      >
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search placeholder="Search name, number or title" allowClear style={{ width: 280 }} onChange={(e) => setSearch(e.target.value)} />
          <Select
            allowClear
            placeholder="Department"
            style={{ width: 220 }}
            value={departmentId}
            onChange={setDepartmentId}
            options={departments.map((d) => ({ value: d.id, label: d.department_name }))}
            showSearch
            optionFilterProp="label"
          />
          <Select
            allowClear
            placeholder="Job description"
            style={{ width: 240 }}
            value={profileId}
            onChange={setProfileId}
            options={profiles.map((p) => ({ value: p.id, label: `${p.code} - ${p.title}` }))}
            showSearch
            optionFilterProp="label"
          />
        </Space>
        <Table
          rowKey="id"
          size="small"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          rowSelection={canManageJd ? { selectedRowKeys: selected, onChange: (k) => setSelected(k as string[]) } : undefined}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        />
      </Card>

      <Modal
        title={`Assign job description to ${selected.length} employee(s)`}
        open={assignOpen}
        okButtonProps={{ disabled: !assignProfile, loading: assign.isPending }}
        onCancel={() => setAssignOpen(false)}
        onOk={async () => {
          await assign.mutateAsync({ profileId: assignProfile as string, ids: selected });
          setAssignOpen(false);
          setSelected([]);
        }}
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Job description"
          value={assignProfile}
          onChange={setAssignProfile}
          options={profiles.filter((p) => Number(p.is_active)).map((p) => ({ value: p.id, label: `${p.code} - ${p.title}` }))}
          showSearch
          optionFilterProp="label"
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Replaces each employee's current job description.
        </Text>
      </Modal>
    </div>
  );
};

export default CompetencyPage;
